import { fetchYowanPublicRecaptchaConfig } from './yowanContentClient';

export const YOWAN_RECAPTCHA_ACTIONS = Object.freeze({
  contactInquiry: 'yowan_contact_inquiry',
  newsSignup: 'yowan_news_signup',
  classApplication: 'yowan_class_application',
  eventApplication: 'yowan_seminar_application',
  seminarApplication: 'yowan_seminar_application',
});

const YOWAN_RECAPTCHA_ERROR_MESSAGES = Object.freeze({
  recaptcha_required: '認証確認に失敗しました。ページを再読み込みしてから、もう一度送信してください。',
  recaptcha_failed: '認証確認に失敗しました。入力内容を確認して、もう一度送信してください。',
  recaptcha_action_mismatch: '認証確認に失敗しました。ページを再読み込みしてから、もう一度送信してください。',
  recaptcha_hostname_mismatch: '認証確認に失敗しました。ページを再読み込みしてから、もう一度送信してください。',
  recaptcha_low_score: '安全確認のため送信できませんでした。時間をおいて、もう一度お試しください。',
  recaptcha_unavailable: '認証確認を開始できませんでした。通信状態を確認して、もう一度お試しください。',
});

const RECAPTCHA_ERROR_CODES = new Set(Object.keys(YOWAN_RECAPTCHA_ERROR_MESSAGES));
const YOWAN_RECAPTCHA_SCRIPT_ID = 'yowan-recaptcha-v3-script';
const YOWAN_RECAPTCHA_EXECUTE_RETRY_DELAY_MS = 350;
const YOWAN_RECAPTCHA_EXECUTE_MAX_ATTEMPTS = 2;

let recaptchaConfigPromise = null;

const readYowanRecaptchaConfig = async () => {
  if (!recaptchaConfigPromise) {
    recaptchaConfigPromise = fetchYowanPublicRecaptchaConfig().catch((error) => {
      recaptchaConfigPromise = null;
      throw error;
    });
  }

  return recaptchaConfigPromise;
};

const resolveActionFromConfig = (action, config = {}) => {
  if (action === YOWAN_RECAPTCHA_ACTIONS.contactInquiry && config.actions?.contactInquiry) {
    return config.actions.contactInquiry;
  }

  if (action === YOWAN_RECAPTCHA_ACTIONS.newsSignup && config.actions?.newsSignup) {
    return config.actions.newsSignup;
  }

  if (action === YOWAN_RECAPTCHA_ACTIONS.classApplication && config.actions?.classApplication) {
    return config.actions.classApplication;
  }

  if (
    (action === YOWAN_RECAPTCHA_ACTIONS.eventApplication ||
      action === YOWAN_RECAPTCHA_ACTIONS.seminarApplication) &&
    (config.actions?.eventApplication || config.actions?.seminarApplication)
  ) {
    return config.actions.eventApplication || config.actions.seminarApplication;
  }

  return action || '';
};

const createRecaptchaUnavailableError = (cause) => {
  const error = new Error(getYowanRecaptchaErrorMessage('recaptcha_unavailable'));
  error.code = 'recaptcha_unavailable';

  if (cause) {
    error.cause = cause;
    error.diagnosticMessage = [cause.name, cause.message].filter(Boolean).join(': ');
  }

  return error;
};

const normalizeRecaptchaError = (error) => {
  if (RECAPTCHA_ERROR_CODES.has(error?.code)) {
    return error;
  }

  const responseErrorCode = readResponseErrorCode(error?.response?.data);

  if (responseErrorCode) {
    const normalizedError = new Error(getYowanRecaptchaErrorMessage(responseErrorCode));
    normalizedError.code = responseErrorCode;
    normalizedError.cause = error;
    return normalizedError;
  }

  return createRecaptchaUnavailableError(error);
};

const readResponseErrorCode = (responseData = {}) => {
  const candidates = [
    responseData.code,
    responseData.error,
    responseData.error_code,
    responseData.recaptcha_code,
    responseData.recaptcha_error,
  ];

  return candidates.find((candidate) => RECAPTCHA_ERROR_CODES.has(candidate)) || '';
};

export const getYowanRecaptchaErrorMessage = (code) =>
  YOWAN_RECAPTCHA_ERROR_MESSAGES[code] || '';

export const getYowanRecaptchaDiagnosticMessage = (error) => {
  const candidates = [
    error?.diagnosticMessage,
    error?.cause?.diagnosticMessage,
    [error?.cause?.name, error?.cause?.message].filter(Boolean).join(': '),
    [error?.name, error?.message].filter(Boolean).join(': '),
  ];

  return candidates
    .map((value) => String(value || '').trim())
    .find((value) => value && !Object.values(YOWAN_RECAPTCHA_ERROR_MESSAGES).includes(value)) || '';
};

export const getYowanRecaptchaResponseErrorMessage = (responseData = {}) => {
  const code = readResponseErrorCode(responseData);

  if (code) {
    return getYowanRecaptchaErrorMessage(code);
  }

  const detail = typeof responseData.detail === 'string' ? responseData.detail : '';

  return RECAPTCHA_ERROR_CODES.has(detail) ? getYowanRecaptchaErrorMessage(detail) : '';
};

const waitForGrecaptchaReady = (grecaptcha) =>
  new Promise((resolve) => {
    grecaptcha.ready(() => resolve(grecaptcha));
  });

const waitForYowanRecaptchaRetry = () =>
  new Promise((resolve) => {
    window.setTimeout(resolve, YOWAN_RECAPTCHA_EXECUTE_RETRY_DELAY_MS);
  });

const executeRecaptchaWithRetry = async (executeToken) => {
  let lastError = null;

  for (let attempt = 1; attempt <= YOWAN_RECAPTCHA_EXECUTE_MAX_ATTEMPTS; attempt += 1) {
    try {
      const token = await executeToken();

      if (token) {
        return token;
      }

      lastError = createRecaptchaUnavailableError();
    } catch (error) {
      lastError = error;
    }

    if (attempt < YOWAN_RECAPTCHA_EXECUTE_MAX_ATTEMPTS) {
      await waitForYowanRecaptchaRetry();
    }
  }

  throw lastError || createRecaptchaUnavailableError();
};

const isYowanRecaptchaScriptReady = (siteKey) => {
  const script = document.getElementById(YOWAN_RECAPTCHA_SCRIPT_ID);

  return (
    script?.dataset?.siteKey === siteKey &&
    script.dataset.loaded === 'true' &&
    window.grecaptcha?.execute &&
    window.grecaptcha?.ready
  );
};

const loadYowanRecaptchaClient = async (siteKey) => {
  if (!siteKey || typeof window === 'undefined' || typeof document === 'undefined') {
    throw createRecaptchaUnavailableError();
  }

  if (isYowanRecaptchaScriptReady(siteKey)) {
    return waitForGrecaptchaReady(window.grecaptcha);
  }

  const existingScript = document.getElementById(YOWAN_RECAPTCHA_SCRIPT_ID);

  if (existingScript?.dataset?.siteKey && existingScript.dataset.siteKey !== siteKey) {
    existingScript.remove();
  }

  if (!document.getElementById(YOWAN_RECAPTCHA_SCRIPT_ID)) {
    const script = document.createElement('script');
    script.id = YOWAN_RECAPTCHA_SCRIPT_ID;
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    script.async = true;
    script.defer = true;
    script.dataset.siteKey = siteKey;
    script.addEventListener('load', () => {
      script.dataset.loaded = 'true';
    });
    document.head.appendChild(script);
  }

  await new Promise((resolve, reject) => {
    const script = document.getElementById(YOWAN_RECAPTCHA_SCRIPT_ID);

    if (!script) {
      reject(createRecaptchaUnavailableError());
      return;
    }

    if (isYowanRecaptchaScriptReady(siteKey)) {
      resolve();
      return;
    }

    script.addEventListener('load', resolve, { once: true });
    script.addEventListener('error', reject, { once: true });
  });

  if (!window.grecaptcha?.execute || !window.grecaptcha?.ready) {
    throw createRecaptchaUnavailableError();
  }

  return waitForGrecaptchaReady(window.grecaptcha);
};

export const executeYowanRecaptcha = async (executeRecaptchaOrAction, maybeAction) => {
  const action =
    typeof executeRecaptchaOrAction === 'function'
      ? maybeAction
      : executeRecaptchaOrAction;
  const executeRecaptcha =
    typeof executeRecaptchaOrAction === 'function'
      ? executeRecaptchaOrAction
      : null;

  try {
    const config = await readYowanRecaptchaConfig();

    if (!config.enabled) {
      return '';
    }

    if (!config.siteKey) {
      throw createRecaptchaUnavailableError();
    }

    const resolvedAction = resolveActionFromConfig(action, config);

    if (!resolvedAction) {
      throw createRecaptchaUnavailableError();
    }

    if (typeof executeRecaptcha !== 'function') {
      const grecaptcha = await loadYowanRecaptchaClient(config.siteKey);
      return executeRecaptchaWithRetry(() =>
        grecaptcha.execute(config.siteKey, { action: resolvedAction }),
      );
    }

    return executeRecaptchaWithRetry(() => executeRecaptcha(resolvedAction));
  } catch (error) {
    throw normalizeRecaptchaError(error);
  }
};
